import { ReactNode } from "react";
import { Box } from "@mui/material";

export type MovePosition =
  | "center"
  | "top-left"
  | "top-center"
  | "top-right"
  | "right-center"
  | "bottom-right"
  | "bottom-center"
  | "bottom-left"
  | "left-center";

type FloatingPanelProps = {
  children: ReactNode;
  hidden?: boolean;
  moveContent: boolean;
  movePosition?: MovePosition;
};

const contentPosition: Record<
  MovePosition,
  {
    left: string | number;
    top: string | number;
    transform: string;
  }
> = {
  center: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  },

  "top-left": {
    top: 0,
    left: 0,
    transform: "translate(0, 0)"
  },
  "top-center": {
    top: 0,
    left: "50%",
    transform: "translate(-50%, 0)"
  },
  "top-right": {
    top: 0,
    left: "100%",
    transform: "translate(-100%, 0)"
  },

  "right-center": {
    top: "50%",
    left: "100%",
    transform: "translate(-100%, -50%)"
  },

  "bottom-right": {
    top: "100%",
    left: "100%",
    transform: "translate(-100%, -100%)"
  },
  "bottom-center": {
    top: "100%",
    left: "50%",
    transform: "translate(-50%, -100%)"
  },
  "bottom-left": {
    top: "100%",
    left: 0,
    transform: "translate(0, -100%)"
  },

  "left-center": {
    top: "50%",
    left: 0,
    transform: "translate(0, -50%)"
  }
};

export default function FloatingPanel({ children, hidden, moveContent, movePosition = "top-right" }: FloatingPanelProps) {
  if (hidden) return null;

  const centerPosition = {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)"
  };

  return (
    <Box
      className='floating-panel'
      sx={{
        position: "absolute",
        ...(moveContent ? contentPosition[movePosition] : centerPosition),
        transition: "all 250ms linear",
        backfaceVisibility: "hidden",

        // disable sliding animations for motion reduced device
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          transform: contentPosition[moveContent ? movePosition : "top-right"].transform
        }
      }}
    >
      {children}
    </Box>
  );
}
